import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import store from '@/store'

const routes: Array<RouteRecordRaw> = [
  // ─── Public ──────────────────────────────────────────────────────────────
  {
    path: '/',
    name: 'home',
    component: () => import('@/views/HomeView.vue'),
    meta: {
      title: "Jenga Hills — Rwanda's #1 Construction Materials Store",
      description: 'Buy cement, steel, roofing, tiles and more from verified suppliers. Delivered directly to your construction site across Rwanda.'
    }
  },
  {
    path: '/shop',
    name: 'shop',
    component: () => import('@/views/shop/ShopView.vue'),
    meta: {
      title: 'Shop Construction Materials — Jenga Hills',
      description: 'Browse cement, steel bars, roofing sheets, tiles, paints and all construction materials. Competitive prices, fast delivery in Rwanda.'
    }
  },
  {
    path: '/shop/:slug',
    name: 'product-detail',
    component: () => import('@/views/shop/ProductDetailView.vue'),
    meta: {
      title: 'Product Details — Jenga Hills',
      description: 'View product specifications, pricing and availability. Order construction materials online and get them delivered to your site in Rwanda.'
    }
  },

  // ─── Public pages ─────────────────────────────────────────────────────────
  {
    path: '/vendors',
    name: 'vendors',
    component: () => import('@/views/VendorsView.vue'),
    meta: {
      title: 'Our Vendors — Jenga Hills',
      description: 'Discover trusted construction material suppliers and vendors on Jenga Hills. Quality verified, ready to deliver across Rwanda.'
    }
  },
  {
    path: '/about',
    name: 'about',
    component: () => import('@/views/AboutView.vue'),
    meta: {
      title: "About Us — Jenga Hills | Building Rwanda's Future",
      description: "Jenga Hills is Rwanda's first dedicated construction e-commerce platform, connecting builders, contractors and homeowners with quality verified materials."
    }
  },
  {
    path: '/contact',
    name: 'contact',
    component: () => import('@/views/ContactView.vue'),
    meta: {
      title: 'Contact Us — Jenga Hills',
      description: 'Get in touch with the Jenga Hills team. We are here to help you find the right construction materials for your project in Rwanda.'
    }
  },

  // ─── Auth ─────────────────────────────────────────────────────────────────
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/auth/LoginView.vue'),
    meta: { guestOnly: true, title: 'Sign In — Jenga Hills', description: 'Sign in to your Jenga Hills account to manage orders and track deliveries.' }
  },
  {
    path: '/register',
    name: 'register',
    component: () => import('@/views/auth/RegisterView.vue'),
    meta: { guestOnly: true, title: 'Create Account — Jenga Hills', description: 'Join Jenga Hills and start ordering quality construction materials online in Rwanda.' }
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('@/views/auth/ForgotPasswordView.vue'),
    meta: { guestOnly: true, title: 'Reset Password — Jenga Hills', description: 'Reset your Jenga Hills account password.' }
  },

  // ─── Customer ─────────────────────────────────────────────────────────────
  {
    path: '/cart',
    name: 'cart',
    component: () => import('@/views/shop/CartView.vue'),
    meta: { title: 'Your Cart — Jenga Hills', description: 'Review your construction materials cart and proceed to checkout.' }
  },
  {
    path: '/checkout',
    name: 'checkout',
    component: () => import('@/views/shop/CheckoutView.vue'),
    meta: { requiresAuth: true, title: 'Checkout — Jenga Hills', description: 'Complete your construction materials order on Jenga Hills.' }
  },
  {
    path: '/orders',
    name: 'order-history',
    component: () => import('@/views/shop/OrderHistoryView.vue'),
    meta: { requiresAuth: true, title: 'My Orders — Jenga Hills', description: 'Track and manage your construction material orders.' }
  },
  {
    path: '/orders/:id',
    name: 'order-detail',
    component: () => import('@/views/shop/OrderDetailView.vue'),
    meta: { requiresAuth: true, title: 'Order Details — Jenga Hills', description: 'View the details and status of your Jenga Hills order.' }
  },
  {
    path: '/profile',
    name: 'profile',
    component: () => import('@/views/account/ProfileView.vue'),
    meta: { requiresAuth: true, title: 'My Profile — Jenga Hills', description: 'View and manage your Jenga Hills profile.' }
  },
  {
    path: '/account/settings',
    name: 'account-settings',
    component: () => import('@/views/account/AccountSettingsView.vue'),
    meta: { requiresAuth: true, title: 'Account Settings — Jenga Hills', description: 'Manage your account settings, password and preferences.' }
  },

  // ─── Vendor (Phase 2) ────────────────────────────────────────────────────
  {
    path: '/vendor/register',
    name: 'vendor-register',
    component: () => import('@/views/vendor/VendorRegisterView.vue'),
    meta: { requiresAuth: true, title: 'Become a Vendor — Jenga Hills', description: 'Register as a supplier on Jenga Hills and reach thousands of builders and contractors across Rwanda.' }
  },
  {
    path: '/vendor/pending',
    name: 'vendor-pending',
    component: () => import('@/views/vendor/VendorPendingView.vue'),
    meta: { requiresAuth: true, title: 'Application Pending — Jenga Hills', description: 'Your vendor application is under review.' }
  },
  {
    path: '/vendor',
    name: 'vendor-dashboard',
    component: () => import('@/views/vendor/VendorDashboardView.vue'),
    meta: { requiresAuth: true, requiresVendor: true, title: 'Vendor Dashboard — Jenga Hills', description: 'Manage your products, orders and earnings on Jenga Hills.' }
  },
  {
    path: '/vendor/products',
    name: 'vendor-products',
    component: () => import('@/views/vendor/VendorProductsView.vue'),
    meta: { requiresAuth: true, requiresVendor: true, title: 'My Products — Jenga Hills Vendor', description: 'Manage your construction material listings on Jenga Hills.' }
  },
  {
    path: '/vendor/orders',
    name: 'vendor-orders',
    component: () => import('@/views/vendor/VendorOrdersView.vue'),
    meta: { requiresAuth: true, requiresVendor: true, title: 'Vendor Orders — Jenga Hills', description: 'View and fulfill incoming orders for your construction materials.' }
  },
  {
    path: '/vendor/earnings',
    name: 'vendor-earnings',
    component: () => import('@/views/vendor/VendorEarningsView.vue'),
    meta: { requiresAuth: true, requiresVendor: true, title: 'My Earnings — Jenga Hills Vendor', description: 'Track your sales revenue and payouts on Jenga Hills.' }
  },
  {
    path: '/vendor/wallet',
    name: 'vendor-wallet',
    component: () => import('@/views/vendor/VendorWalletView.vue'),
    meta: { requiresAuth: true, requiresVendor: true, title: 'My Wallet — Jenga Hills Vendor', description: 'View your wallet balance and transaction history.' }
  },

  // ─── Admin ────────────────────────────────────────────────────────────────
  {
    path: '/admin',
    name: 'admin-dashboard',
    component: () => import('@/views/admin/AdminDashboardView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Admin Dashboard — Jenga Hills', description: 'Jenga Hills admin control panel.' }
  },
  {
    path: '/admin/products',
    name: 'admin-products',
    component: () => import('@/views/admin/AdminProductsView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Manage Products — Jenga Hills Admin', description: 'Jenga Hills admin: manage all product listings.' }
  },
  {
    path: '/admin/orders',
    name: 'admin-orders',
    component: () => import('@/views/admin/AdminOrdersView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Manage Orders — Jenga Hills Admin', description: 'Jenga Hills admin: view and manage all customer orders.' }
  },
  {
    path: '/admin/vendors',
    name: 'admin-vendors',
    component: () => import('@/views/admin/AdminVendorsView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Manage Vendors — Jenga Hills Admin', description: 'Jenga Hills admin: approve and manage vendor accounts.' }
  },
  {
    path: '/admin/vendors/:id',
    name: 'admin-vendor-detail',
    component: () => import('@/views/admin/AdminVendorDetailView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Vendor Detail — Jenga Hills Admin', description: 'Jenga Hills admin: view vendor details and products.' }
  },
  {
    path: '/admin/vendor-products',
    name: 'admin-vendor-products',
    component: () => import('@/views/admin/AdminVendorProductsView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Vendor Products — Jenga Hills Admin', description: 'Jenga Hills admin: review and manage vendor product listings.' }
  },
  {
    path: '/admin/wallets',
    name: 'admin-wallets',
    component: () => import('@/views/admin/AdminWalletsView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Vendor Wallets — Jenga Hills Admin', description: 'Jenga Hills admin: view all vendor wallet balances.' }
  },

  // ─── Catch-all ────────────────────────────────────────────────────────────
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { title: 'Page Not Found — Jenga Hills', description: 'The page you are looking for does not exist.' }
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior (_to, _from, savedPosition) {
    if (savedPosition) return savedPosition
    return { top: 0 }
  }
})

router.beforeEach((to, _from, next) => {
  const isAuthenticated = store.getters['auth/isAuthenticated']
  const isAdmin = store.getters['auth/isAdmin']
  const isVendor = store.getters['auth/isVendor']

  if (to.meta.requiresAuth && !isAuthenticated) {
    return next({ name: 'login', query: { redirect: to.fullPath } })
  }
  if (to.meta.requiresAdmin && !isAdmin) {
    return next({ name: 'home' })
  }
  if (to.meta.requiresVendor && !isVendor && !isAdmin) {
    return next({ name: 'vendor-register' })
  }
  if (to.meta.guestOnly && isAuthenticated) {
    return next({ name: 'home' })
  }

  next()
})

router.afterEach((to) => {
  const title = to.meta.title as string | undefined
  const description = to.meta.description as string | undefined

  document.title = title ?? "Jenga Hills — Rwanda's #1 Construction Materials Store"

  const metaDesc = document.querySelector('meta[name="description"]')
  if (metaDesc && description) metaDesc.setAttribute('content', description)

  const ogTitle = document.querySelector('meta[property="og:title"]')
  if (ogTitle && title) ogTitle.setAttribute('content', title)

  const ogDesc = document.querySelector('meta[property="og:description"]')
  if (ogDesc && description) ogDesc.setAttribute('content', description)

  const ogUrl = document.querySelector('meta[property="og:url"]')
  if (ogUrl) ogUrl.setAttribute('content', window.location.href)

  const twitterTitle = document.querySelector('meta[name="twitter:title"]')
  if (twitterTitle && title) twitterTitle.setAttribute('content', title)

  const twitterDesc = document.querySelector('meta[name="twitter:description"]')
  if (twitterDesc && description) twitterDesc.setAttribute('content', description)
})

export default router
